import { useTranslation } from '@thoughtspot/i18n';
import { ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { WizardModal, } from '@thoughtspot/radiant-react/widgets/wizard-modal';
import { getGuidsToSkip, hasPrivilege, PRIVILEGES, } from '@thoughtspot/session-service';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { Admin__FeatureManagementCategory, Admin__FeatureManagementScope, useGetFeatureManagementLazyQuery, } from '../../../../services/feature-management/feature-management-service';
import { ListCategory, OrgDetailType, } from '../../../../services/generated/graphql-types';
import { useCreateOrgMutation, useGetOrgDetailLazyQuery, useUpdateOrgMutation, } from '../../../../services/org/org-service';
import { useGetUsersListPrincipalQuery } from '../../../../services/user-group/user-group-service';
import { OrgManagementTranslationKeys } from '../../constants';
import { DESCRIPTION_MAX_LENGTH, ORG_NAME_MAX_LENGTH, SEARCH_DEBOUNCE_MS, } from '../org-management.utils';
import styles from './org-create-wizard.module.scss';
import { StepAssignUsers } from './step-assign-users';
import { StepFeatureConfig } from './step-feature-config';
import { StepOrgDetails } from './step-org-details';
const getFeatureValue = (isEditMode, orgFeatureIdSet, feature) => {
    if (isEditMode) {
        return orgFeatureIdSet.has(feature.featureId) ? 'true' : 'false';
    }
    return feature.elementValue ?? 'false';
};
export const OrgCreateWizard = ({ isOpen, org, onClose, onSuccess, onError, }) => {
    const isEditMode = !!org;
    const { t } = useTranslation();
    const [currentStep, setCurrentStep] = useState(0);
    const isAdmin = hasPrivilege(PRIVILEGES.ADMINISTRATION);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [nameError, setNameError] = useState(null);
    const [descriptionError, setDescriptionError] = useState(null);
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [userSearchText, setUserSearchText] = useState('');
    const [featureSearchTerm, setFeatureSearchTerm] = useState('');
    const [allFeatures, setAllFeatures] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { createOrg } = useCreateOrgMutation();
    const { updateOrg } = useUpdateOrgMutation();
    const { data: orgDetailData, getOrgDetail } = useGetOrgDetailLazyQuery();
    const { data: orgFlagData, getOrgDetail: getOrgDetailForFlags, } = useGetOrgDetailLazyQuery();
    const { data: usersData, loading: usersLoading, refetch: refetchUsers, } = useGetUsersListPrincipalQuery({
        batchSize: -1,
        category: ListCategory.All,
        isAllOrgContext: true,
        offset: 0,
        pattern: '',
        skipIds: getGuidsToSkip(),
        sort: 'NAME',
        sortAscending: true,
    });
    const searchDebounceRef = useRef();
    const handleUserSearchChange = useCallback((val) => {
        setUserSearchText(val);
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = setTimeout(() => {
            refetchUsers({
                data: {
                    batchSize: 200,
                    category: ListCategory.All,
                    isAllOrgContext: true,
                    offset: 0,
                    pattern: val || '',
                    skipIds: getGuidsToSkip(),
                    sort: 'NAME',
                    sortAscending: true,
                },
            });
        }, SEARCH_DEBOUNCE_MS);
    }, [refetchUsers]);
    useEffect(() => () => clearTimeout(searchDebounceRef.current), []);
    const { data: featureData, loading: featuresLoading, getFeatureManagement, } = useGetFeatureManagementLazyQuery();
    useEffect(() => {
        if (isOpen) {
            getFeatureManagement(undefined, Admin__FeatureManagementCategory.GeneralAccess, Admin__FeatureManagementScope.Cluster);
        }
    }, [isOpen, getFeatureManagement]);
    useEffect(() => {
        if (isOpen && isEditMode && org) {
            getOrgDetail({
                orgid: parseInt(org.id, 10),
                type: OrgDetailType.User,
                batchsize: -1,
                offset: -1,
            });
            getOrgDetailForFlags({
                orgid: parseInt(org.id, 10),
                type: OrgDetailType.FeatureFlag,
                batchsize: -1,
                offset: -1,
            });
        }
    }, [isOpen, isEditMode, org]);
    const orgFeatureIdSet = useMemo(() => {
        const set = new Set();
        (orgFlagData?.Data?.featureFlags ?? []).forEach((group) => {
            (group?.features ?? []).forEach((feature) => {
                if (feature?.featureId) {
                    set.add(feature.featureId);
                }
            });
        });
        return set;
    }, [orgFlagData]);
    useEffect(() => {
        if (!featureData?.data)
            return;
        const seen = new Set();
        const mapped = featureData.data.flatMap((group) => (group.features ?? []).flatMap((feature) => {
            if (seen.has(feature.featureId))
                return [];
            seen.add(feature.featureId);
            return [
                {
                    configName: feature.featureId,
                    orgIds: [],
                    elementType: 'toggle',
                    value: getFeatureValue(isEditMode, orgFeatureIdSet, feature),
                    category: group.groupName ?? 'Other',
                },
            ];
        }));
        setAllFeatures(mapped);
    }, [featureData, orgFeatureIdSet, isEditMode]);
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setName(org?.name ?? '');
            setDescription(org?.description ?? '');
            setNameError(null);
            setDescriptionError(null);
            setSelectedUserIds(new Set());
            setUserSearchText('');
            setFeatureSearchTerm('');
            setAllFeatures([]);
            setIsSubmitting(false);
        }
    }, [isOpen, org]);
    useEffect(() => {
        if (!isEditMode || !orgDetailData)
            return;
        const users = orgDetailData?.Data?.users?.data ?? [];
        setSelectedUserIds(new Set(users.map(u => u?.id).filter(Boolean)));
    }, [orgDetailData, isEditMode]);
    const userItems = useMemo(() => (usersData?.objects ?? [])
        .filter((u) => u.name !== 'tsadmin')
        .map((u) => ({
        id: u.id ?? '',
        label: u.displayName ?? u.name ?? '',
    })), [usersData]);
    const validateStep1 = useCallback(() => {
        const trimmed = name.trim();
        if (!trimmed) {
            setNameError(t(OrgManagementTranslationKeys.WIZARD_ORG_NAME_REQUIRED));
            return false;
        }
        if (trimmed.length > ORG_NAME_MAX_LENGTH) {
            setNameError(t(OrgManagementTranslationKeys.WIZARD_ORG_NAME_MAX_LIMIT, {
                field_name: t(OrgManagementTranslationKeys.COL_ORG_NAME),
            }));
            return false;
        }
        setNameError(null);
        if (description.trim().length > DESCRIPTION_MAX_LENGTH) {
            setDescriptionError(t(OrgManagementTranslationKeys.WIZARD_DESC_MAX_LIMIT, {
                field_name: t(OrgManagementTranslationKeys.WIZARD_DESCRIPTION_LABEL),
            }));
            return false;
        }
        setDescriptionError(null);
        return true;
    }, [name, description, t]);
    const handleFeatureValueChange = useCallback((configName, newValue) => {
        setAllFeatures(prev => prev.map(f => f.configName === configName ? { ...f, value: newValue } : f));
    }, []);
    const submitButtonText = isEditMode
        ? t(OrgManagementTranslationKeys.WIZARD_SAVE)
        : t(OrgManagementTranslationKeys.WIZARD_ADD);
    const handleNextStep = useCallback(() => setCurrentStep(prev => prev + 1), []);
    const handlePrevStep = useCallback(() => setCurrentStep(prev => prev - 1), []);
    const handleStep1Next = useCallback(() => {
        if (!validateStep1())
            return;
        handleNextStep();
    }, [validateStep1, handleNextStep]);
    const handleFinalSubmit = useCallback(async () => {
        if (!validateStep1()) {
            setCurrentStep(0);
            return;
        }
        setIsSubmitting(true);
        try {
            const enabledFeatureIds = allFeatures
                .filter(f => f.value === 'true')
                .map(f => f.configName);
            if (isEditMode) {
                await updateOrg({
                    orgid: org?.id ?? '',
                    name: name.trim(),
                    description: description.trim(),
                    ...(isAdmin && {
                        userids: Array.from(selectedUserIds),
                    }),
                    ...(isAdmin && {
                        feature_identifiers: enabledFeatureIds,
                    }),
                });
            }
            else {
                await createOrg({
                    name: name.trim(),
                    description: description.trim(),
                    ...(isAdmin &&
                        selectedUserIds.size > 0 && {
                        userids: Array.from(selectedUserIds),
                    }),
                    ...(isAdmin &&
                        enabledFeatureIds.length > 0 && {
                        feature_identifiers: enabledFeatureIds,
                    }),
                });
            }
            onSuccess(isEditMode ? 'edit' : 'create');
        }
        catch (err) {
            const errors = err?.networkError?.result?.errors ??
                err?.graphQLErrors;
            let debugMessage = errors?.[0]?.extensions?.exception?.upstreamResponse?.data
                ?.debug ??
                err?.message ??
                'An unexpected error occurred';
            try {
                const parsed = JSON.parse(debugMessage);
                if (Array.isArray(parsed)) {
                    debugMessage = parsed.join(', ');
                }
            }
            catch {
            }
            onError?.(debugMessage);
        }
        finally {
            setIsSubmitting(false);
        }
    }, [
        validateStep1,
        isEditMode,
        org,
        updateOrg,
        createOrg,
        name,
        description,
        selectedUserIds,
        allFeatures,
        isAdmin,
        onSuccess,
        onError,
    ]);
    const wizardSteps = [
        {
            stepTitle: isEditMode
                ? t(OrgManagementTranslationKeys.WIZARD_EDIT_CREDENTIALS_TITLE)
                : t(OrgManagementTranslationKeys.WIZARD_STEP1_TITLE),
            stepContent: (React.createElement(StepOrgDetails, { name: name, description: description, nameError: nameError, descriptionError: descriptionError, onNameChange: e => {
                    setName(e.target.value);
                    if (nameError)
                        setNameError(null);
                }, onDescriptionChange: e => {
                    setDescription(e.target.value);
                    if (descriptionError)
                        setDescriptionError(null);
                } })),
            stepForwardButton: {
                text: isAdmin
                    ? t(OrgManagementTranslationKeys.WIZARD_NEXT)
                    : submitButtonText,
                onNext: isAdmin ? handleStep1Next : handleFinalSubmit,
                isDisabled: isSubmitting,
                ...(!isAdmin && {
                    isLoading: isSubmitting,
                }),
            },
        },
        ...(isAdmin
            ? [
                {
                    stepTitle: t(OrgManagementTranslationKeys.WIZARD_STEP2_TITLE),
                    stepContent: (React.createElement(StepAssignUsers, { users: userItems, usersLoading: usersLoading, selectedUserIds: selectedUserIds, onUserSelectionChange: setSelectedUserIds, searchText: userSearchText, onSearchChange: handleUserSearchChange })),
                    stepForwardButton: {
                        text: t(OrgManagementTranslationKeys.WIZARD_NEXT),
                        onNext: handleNextStep,
                        isDisabled: isSubmitting,
                    },
                    stepBackButton: {
                        text: t(OrgManagementTranslationKeys.WIZARD_BACK),
                        onBack: handlePrevStep,
                    },
                },
            ]
            : []),
        ...(isAdmin
            ? [
                {
                    stepTitle: t(OrgManagementTranslationKeys.WIZARD_STEP3_TITLE),
                    stepContent: (React.createElement(StepFeatureConfig, { features: allFeatures, loading: featuresLoading, searchTerm: featureSearchTerm, onSearchChange: setFeatureSearchTerm, onFeatureValueChange: handleFeatureValueChange })),
                    stepForwardButton: {
                        text: isEditMode
                            ? t(OrgManagementTranslationKeys.WIZARD_SAVE)
                            : t(OrgManagementTranslationKeys.WIZARD_ADD),
                        onNext: handleFinalSubmit,
                        isDisabled: isSubmitting,
                        isLoading: isSubmitting,
                    },
                    stepBackButton: {
                        text: t(OrgManagementTranslationKeys.WIZARD_BACK),
                        onBack: handlePrevStep,
                    },
                },
            ]
            : []),
    ];
    return (React.createElement(WizardModal, { isOpen: isOpen, size: ModalSize.MEDIUM3, title: isEditMode
            ? t(OrgManagementTranslationKeys.WIZARD_MODAL_TITLE_EDIT)
            : t(OrgManagementTranslationKeys.ADD_NEW_ORG), onDismiss: onClose, denyButton: {
            text: t(OrgManagementTranslationKeys.WIZARD_CANCEL),
            onDeny: onClose,
        }, wizardModalStep: wizardSteps, currentStep: currentStep, isProgressBarEnabled: true, includesHeader: true, includesFooter: true, className: styles.orgCreateWizardModal, customHeaderClassName: styles.wizardHeader, customStepsClassName: styles.wizardContent, customFooterClassName: styles.wizardFooter }));
};
//# sourceMappingURL=org-create-wizard.js.map